import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Axios } from '../axios-instance';
import { BaseError } from '@utils/types/shared/base';
import {
  CreateLicenseRequestPayload,
  LicenseRequestResponse
} from '@utils/types/resellers/license-request';
import { RESELLER_LICENSE_REQUESTS_QUERY_KEY } from './use-get-license-requests';

const URL_EXTENSION = 'api/v3';

interface CreateLicenseRequestParams {
  customerId: string;
  payload: CreateLicenseRequestPayload;
}

const createLicenseRequest = async ({
  customerId,
  payload
}: CreateLicenseRequestParams): Promise<LicenseRequestResponse> => {
  const response = await Axios.post<LicenseRequestResponse>(
    `${URL_EXTENSION}/reseller/customer/${customerId}/license-request`,
    payload
  );
  return response.data;
};

export const useCreateLicenseRequest = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    LicenseRequestResponse,
    AxiosError<BaseError>,
    CreateLicenseRequestParams
  >(createLicenseRequest, {
    onSuccess: (_, variables) => {
      toast.success(t('license-request-created'));
      queryClient.invalidateQueries([
        RESELLER_LICENSE_REQUESTS_QUERY_KEY,
        variables.customerId
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const data = error.response?.data;
      let errorMessage = t('failed-to-create-license-request');

      // Handle specific error codes
      if (data?.error === 'duplicate_pending') {
        errorMessage = t('duplicate-pending-request');
      } else if (data?.error_description) {
        errorMessage = data.error_description;
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (data?.detail) {
        errorMessage = data.detail;
      }

      toast.error(errorMessage);
    }
  });
};
